import * as React from 'react';

import {
    Box,
    Button,
    ButtonProps,
    CircularProgress,
    type PaletteColor,
    type SxProps,
    Theme,
    Tooltip,
    type TooltipProps,
} from '@mui/material';
import { FaOptionIcon, NamedIconsType } from '@europrocurement/l2d-icons';
import { type ComponentColorResult } from '@europrocurement/flexy-components/hooks';
import {
    type ActionButtonKey,
    type ComponentTokenColorMap,
} from '../../../assets/global/componentTokens';
import { type L2DPalette, useL2DTheme } from '../../../hooks/useL2DTheme';
import { TextIcon } from '../TextIcon';

/** @deprecated Use {@link ActionButtonKey} from componentTokens instead. */
export type ActionButtonVariant = ActionButtonKey;

// Event props forwarded to the span wrapper so parent Tooltip components work on disabled buttons
type TooltipForwardedProps = Pick<
    React.HTMLAttributes<HTMLElement>,
    | 'onMouseEnter'
    | 'onMouseLeave'
    | 'onMouseOver'
    | 'onFocus'
    | 'onBlur'
    | 'onTouchStart'
    | 'onTouchEnd'
>;

export type FlexyButtonProps = {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    label?: string;
    /**
     * Contenu du bouton. Accepte tout ReactNode.
     * Si `icon` est aussi fourni, le contenu est enveloppé dans `TextIcon`.
     */
    children?: React.ReactNode;
    /**
     * Couleur du bouton.
     * - Clé sémantique (`'primary'`, `'secondary'`, `'danger'`, `'warning'`) → tokens `palette.actionButton`
     * - `PaletteColor` (objet MUI avec `.main`) → couleur directe
     * - `string` CSS (ex : `'#000000'`) → couleur directe
     */
    color?: string | PaletteColor | L2DPalette | ComponentColorResult;
    /** Icône FontAwesome. Si seule (sans `children`), produit un bouton carré. */
    icon?: NamedIconsType;
    disabled?: boolean;
    loading?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: 'contained' | 'outlined' | 'text';
    /** Surcharge de styles via SxProps MUI — mergé par-dessus les styles internes. */
    sx?: SxProps<Theme>;
    className?: string;
    type?: ButtonProps['type'];
    /** Texte affiché dans un Tooltip au survol. Le span wrapper est géré en interne, même pour les boutons désactivés. */
    tooltip?: string;
    /** Placement du tooltip. Par défaut : comportement MUI (bottom). */
    tooltipPlacement?: TooltipProps['placement'];
    'aria-label'?: string;
} & TooltipForwardedProps;

const isPaletteColor = (
    color: ComponentColorResult | L2DPalette | PaletteColor | string,
): color is PaletteColor => typeof color === 'object' && color !== null && 'main' in color;

const isComponentColorResult = (
    color: ComponentColorResult | L2DPalette | PaletteColor | string,
): color is ComponentColorResult =>
    typeof color === 'object' && color !== null && 'backgroundColor' in color;

type ResolvedColor = {
    main: string;
    light: string;
    text?: string;
    textOnHover?: string;
};

const sizeConfig = {
    small: { button: 32, icon: 16, borderRadius: 12 },
    medium: { button: 40, icon: 20, borderRadius: 14 },
    large: { button: 48, icon: 24, borderRadius: 15 },
};

export const FlexyButton = React.forwardRef<HTMLElement, FlexyButtonProps>(
    (
        {
            onClick,
            label,
            children,
            color,
            icon,
            disabled = false,
            loading = false,
            size = 'small',
            variant = 'contained',
            type = 'button',
            sx,
            className,
            tooltip,
            tooltipPlacement,
            'aria-label': ariaLabel,
            onMouseEnter,
            onMouseLeave,
            onMouseOver,
            onFocus,
            onBlur,
            onTouchStart,
            onTouchEnd,
        },
        ref,
    ) => {
        const { palette } = useL2DTheme();
        const config = sizeConfig[size];
        const isDisabled = disabled || loading;

        const resolvedColor = React.useMemo((): ResolvedColor => {
            if (isDisabled) {
                return { main: palette.action.disabledBackground, light: palette.action.disabled };
            }

            const target = color ?? 'primary';

            if (typeof target === 'string') {
                const actionToken = (palette.actionButton as ComponentTokenColorMap)?.[target];
                if (actionToken) {
                    return {
                        main: actionToken.backgroundColor,
                        light: actionToken.backgroundColorOnHover,
                        text: actionToken.textColor,
                        textOnHover: actionToken.textColorOnHover,
                    };
                }
                // If target is a palette key (e.g. 'primary') but actionButton isn't available,
                // resolve via the standard MUI palette entry.
                const paletteEntry = (palette as unknown as Record<string, unknown>)[target];
                if (paletteEntry && typeof paletteEntry === 'object' && 'main' in paletteEntry) {
                    const c = paletteEntry as { main: string; light?: string };
                    return { main: c.main, light: c.light ?? c.main };
                }
                return { main: target, light: target };
            }

            if (isComponentColorResult(target)) {
                return {
                    main: target.backgroundColor,
                    light: target.backgroundColorOnHover,
                    text: target.textColor,
                    textOnHover: target.textColorOnHover,
                };
            }

            if (isPaletteColor(target)) {
                return { main: target.main, light: target.light ?? target.main };
            }

            return { main: palette.primary.main, light: palette.primary.light };
        }, [color, isDisabled, palette]);

        // label takes precedence over children; if both are provided label wins
        const content: React.ReactNode = label ?? children ?? null;
        const isIconOnly = Boolean(icon) && !content && !loading;
        const hasIconAndContent = Boolean(icon) && Boolean(content);

        const buttonSx = React.useMemo((): SxProps<Theme> => {
            const base: Record<string, unknown> = {
                verticalAlign: 'middle',
                transition: 'all ease-in-out 300ms',
                borderRadius: `${config.borderRadius}px`,
            };

            if (isIconOnly) {
                Object.assign(base, {
                    minWidth: 'unset',
                    width: config.button,
                    height: config.button,
                    padding: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                });
            } else {
                Object.assign(base, { padding: '8px 12px' });
            }

            if (isDisabled) {
                Object.assign(base, {
                    color: palette.text.disabled,
                    backgroundColor: 'transparent',
                    cursor: 'not-allowed',
                    opacity: 0.6,
                });
            } else if (variant === 'contained') {
                Object.assign(base, {
                    color: resolvedColor.text ?? palette.getContrastText(resolvedColor.main),
                    backgroundColor: resolvedColor.main,
                });
            } else {
                // Sans fond : la couleur du token (main) sert de couleur de texte.
                // resolvedColor.text est le contraste sur fond coloré — inutilisable ici.
                Object.assign(base, {
                    color: resolvedColor.main,
                    backgroundColor: 'transparent',
                    border: variant === 'outlined' ? `1px solid ${resolvedColor.main}` : 'none',
                });
            }

            if (!isDisabled && variant !== 'text') {
                const hoverBg =
                    variant === 'contained' ? resolvedColor.light : `${resolvedColor.main}14`;
                const hover: Record<string, unknown> = { backgroundColor: hoverBg };
                if (variant === 'contained' && resolvedColor.textOnHover) {
                    hover.color = resolvedColor.textOnHover;
                }
                if (variant === 'outlined') {
                    hover.borderColor = resolvedColor.main;
                }
                Object.assign(base, { '&:hover': hover });
            }

            return base as SxProps<Theme>;
        }, [config, isIconOnly, isDisabled, variant, resolvedColor, palette]);

        const iconColor = isDisabled ? palette.action.disabled : resolvedColor.main;

        const renderContent = () => {
            if (loading) {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-evenly',
                            gap: '8px',
                        }}
                    >
                        <CircularProgress
                            size={config.icon}
                            color="inherit"
                        />
                        {content}
                    </Box>
                );
            }

            if (isIconOnly) {
                return (
                    <FaOptionIcon
                        icon={icon!.props.icon}
                        sx={{
                            opacity: isDisabled ? 0.6 : 1,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: iconColor,
                        }}
                    />
                );
            }

            if (hasIconAndContent) {
                return (
                    <TextIcon
                        gap="8px"
                        icon={icon!}
                        sx={{
                            opacity: isDisabled ? 0.6 : 1,
                            color: isDisabled ? palette.text.disabled : undefined,
                        }}
                    >
                        {content}
                    </TextIcon>
                );
            }

            return content;
        };

        const button = (
            <Button
                type={type}
                variant={variant}
                size={size}
                onClick={onClick ?? undefined}
                disabled={isDisabled}
                className={className}
                aria-label={ariaLabel}
                sx={[buttonSx, ...(Array.isArray(sx) ? sx : [sx]).filter(Boolean)]}
            >
                {renderContent()}
            </Button>
        );

        // When tooltip is provided or button is disabled: wrap in span so Tooltip (own or parent) works.
        // Disabled buttons don't fire pointer events; the span absorbs them instead.
        if (tooltip !== undefined || isDisabled) {
            const wrapper = (
                <span
                    ref={ref as React.Ref<HTMLSpanElement>}
                    style={{ display: 'inline-flex' }}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onMouseOver={onMouseOver}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {button}
                </span>
            );

            if (tooltip !== undefined) {
                return (
                    <Tooltip
                        title={tooltip}
                        placement={tooltipPlacement}
                    >
                        {wrapper}
                    </Tooltip>
                );
            }

            return wrapper;
        }

        return button;
    },
);
